import clsx from "clsx";
import React from "react";
import { EditorialProductCard } from "web/react/components/editorials/editorial-product-card";
import { Heading } from "web/react/emo/heading";
import { ImageContainer } from "web/react/emo/image-container";
import { RichText } from "web/react/emo/rich-text";
import { useEditorialAnalytics } from "web/react/hooks/use-editorial-analytics";
import history from "web/script/utils/history";
import { EditorialShoppableSerializer } from "web/types/serializers";
import * as styles from "./editorial-shoppable.css";

export interface EditorialShoppableProps extends EditorialShoppableSerializer {
    className?: string;
    editorialLabel: string;
    franchise: string;
    tags: string[];
    position: number;
    isSponsored: boolean;
}

export function EditorialShoppable({
    id: componentId,
    title,
    body,
    image_url: imageUrl,
    image_placement: imagePlacement = "left",
    image_credit: imageCredit,
    product_cards: productCards,
    editorialLabel,
    franchise,
    tags,
    position,
    isSponsored,
    className,
}: EditorialShoppableProps): React.ReactElement {
    const { containerRef: visibilityRef } = useEditorialAnalytics({
        label: "shoppable_block",
        componentId,
        editorialLabel,
        franchise,
        tags,
        position,
        isSponsored,
        visibleThreshold: 0.5,
    });

    function updateURLID(id): void {
        const url = new URL(`${window.location.origin}${window.location.pathname}#${id}`);
        history.replaceState(url);
    }

    const titleId = title
        ? title.trim().replace(/\s+/g, "-").toLowerCase()
        : `shoppable-${position}`;

    return (
        <section className={className}>
            <div
                className={clsx(styles.container, {
                    [styles.reverseContainer]: imagePlacement === "right",
                })}
            >
                {imageUrl && (
                    <div className={styles.image} ref={visibilityRef}>
                        <ImageContainer imageSrc={imageUrl} imageCredit={imageCredit} />
                    </div>
                )}
                <div className={styles.content}>
                    {(title || body) && (
                        <div
                            id={titleId}
                            className={styles.textContent}
                            onClick={() => updateURLID(titleId)}
                        >
                            {title && (
                                <Heading
                                    className={styles.title}
                                    textStyle="title-l"
                                    as="h2"
                                    upperCase
                                >
                                    {title}
                                </Heading>
                            )}
                            {body && (
                                <RichText
                                    className={styles.body}
                                    textStyle={{ sm: "body-s", md: "body" }}
                                    color="secondary"
                                >
                                    {body}
                                </RichText>
                            )}
                        </div>
                    )}
                    <div className={styles.productGrid}>
                        {productCards.map((product, key) => (
                            <EditorialProductCard
                                key={product.id}
                                position={position}
                                productPosition={key + 1}
                                product={product}
                                franchise={franchise}
                                tags={tags}
                                componentId={componentId}
                                editorialLabel={editorialLabel}
                                isSponsored={isSponsored}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
